import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { QuickNoteRow } from '../components/QuickNoteGroupsCard';
import { QuickNoteDialog } from '../components/QuickNoteDialogs';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_ROLES = ['system_admin', 'executive', 'operations_manager'];

export default function QuickNoteGroupPage() {
  const { groupId } = useParams();
  const { user } = useAuth();
  const canManage = ADMIN_ROLES.includes(user?.role);
  const [group, setGroup] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState({ open: false, note: null });

  const load = async () => {
    try {
      const [g, t] = await Promise.all([axios.get(`${API}/settings/quick-note-groups`), axios.get(`${API}/admin/templates`)]);
      setGroup(g.data.find(x => x.id === groupId) || null);
      setTemplates((t.data.templates || []).filter(x => x.is_active !== false));
    } catch (e) {
      toast.error('Failed to load quick notes');
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [groupId]);

  const templateName = (id) => templates.find(t => t.id === id)?.name || 'Missing template';
  const run = async (fn, msg) => {
    setSaving(true);
    try {
      await fn();
      toast.success(msg);
      setDialog({ open: false, note: null });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Something went wrong');
    } finally {
      setSaving(false);
    }
  };
  const saveNote = (data) => run(() => axios.put(`${API}/settings/quick-note-groups/${groupId}/notes/${dialog.note.id}`, data), 'Quick note updated');
  const deleteNote = (n) => window.confirm(`Delete the quick note "${n.name}"?`) &&
    run(() => axios.delete(`${API}/settings/quick-note-groups/${groupId}/notes/${n.id}`), 'Quick note deleted');

  if (loading) return <div className="p-6 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="quick-note-group-page">
      <div>
        <Link to="/templates" className="inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid="quick-note-group-back-link">
          <ArrowLeft className="w-4 h-4" />Back to Templates
        </Link>
        <h1 className="text-2xl font-bold mt-2" data-testid="quick-note-group-title">{group?.name || 'Quick notes'}</h1>
        <p className="text-muted-foreground">Click a quick note to edit it. Each quick note creates one case note per assigned template.</p>
      </div>
      {!group ? (
        <p className="text-muted-foreground" data-testid="quick-note-group-not-found">This quick note group was not found.</p>
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 flex-wrap">
              Quick notes
              <Badge variant="secondary">{group.case_type_name}</Badge>
            </CardTitle>
            <CardDescription>{group.notes?.length || 0} quick notes in this group</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {group.notes?.length ? group.notes.map(n => (
              <div key={n.id} className={canManage ? 'cursor-pointer' : ''} onClick={(e) => canManage && !e.target.closest('button') && setDialog({ open: true, note: n })}>
                <QuickNoteRow note={n} templateName={templateName} canManage={canManage}
                  onEdit={() => setDialog({ open: true, note: n })} onDelete={() => deleteNote(n)} />
              </div>
            )) : <p className="text-sm text-muted-foreground">No quick notes yet</p>}
          </CardContent>
        </Card>
      )}
      <QuickNoteDialog open={dialog.open} note={dialog.note} templates={templates} saving={saving}
        onOpenChange={(o) => setDialog(s => ({ ...s, open: o }))} onSave={saveNote} />
    </div>
  );
}
